import { useContext, useState } from 'react';
import { ColumnContext, FilterContext } from 'utils/context';
import { ModalVariant } from 'utils/types/saved-view';

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Flex,
  Spinner,
  TextField,
  Typography,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';
import {
  IBasicInventoryOrderSearchCriteria,
  IShipmentSearchCriteria,
  SavedView,
} from '@project44-models/shipment-list';

interface ModalConfig {
  heading: string;
  buttonText: string;
  buttonColor?: 'danger' | 'primary' | 'neutral';
  onConfirm: () => void;
  description: React.ReactNode;
  readOnly?: boolean;
  disabled?: boolean;
  textFieldProps?: { isDisabled?: boolean };
}

interface ViewModalProps {
  createView: (body: SavedView) => Promise<{ views: SavedView[] }>;
  doSearch: () => void;
  getViewRequest: () =>
    | IShipmentSearchCriteria
    | IBasicInventoryOrderSearchCriteria;
  loading: boolean;
  onClose: () => void;
  revertView: () => void;
  selectedView?: SavedView;
  updateView: (id: string, body: SavedView) => Promise<{ views: SavedView[] }>;
  variant: ModalVariant;
  viewType: 'order' | 'shipment';
}

function ViewModal({
  createView,
  doSearch,
  getViewRequest,
  loading,
  onClose,
  revertView,
  selectedView,
  updateView,
  variant,
  viewType,
}: ViewModalProps) {
  const { t, t__react } = useTranslation();
  const [name, setName] = useState(selectedView?.name || '');
  const { setFiltersKey } = useContext(FilterContext);
  const { viewColumns } = useContext(ColumnContext);

  const isOrder = viewType === 'order';

  const handleClose = () => {
    setName('');
    onClose();
  };

  const config: {
    create: ModalConfig;
    reset: ModalConfig;
    update: ModalConfig;
  } = {
    create: {
      heading: t('core.saveAsNewView'),
      buttonText: t('global.confirm'),
      disabled: !name,
      description: t('core.saveAsNewViewDescription'),
      textFieldProps: { isDisabled: loading },
      onConfirm: async () => {
        const filterCriteria = getViewRequest();
        const res = await createView({
          name,
          filterCriteria,
          displayOptions: { activeColumns: viewColumns },
        });
        if (res?.views?.length && res.views[0].id) {
          setFiltersKey('listView', res.views[0].id);
        }
        handleClose();
      },
    },
    reset: {
      heading: t('core.resetView'),
      buttonText: t('global.reset'),
      buttonColor: 'danger',
      description: t__react('core.revertViewBody', {
        viewName: <b>"{selectedView?.name}"</b>,
      }),
      readOnly: true,
      onConfirm: () => {
        revertView();
        doSearch();
        onClose();
      },
    },
    update: {
      heading: t('core.updateView'),
      buttonText: t('global.update'),
      description: t__react('core.updateViewBody', {
        viewName: <b>"{selectedView?.name}"</b>,
      }),
      readOnly: true,
      onConfirm: async () => {
        if (selectedView?.id) {
          await updateView(selectedView.id, {
            id: selectedView.id,
            name: selectedView.name,
            filterCriteria: getViewRequest(),
            displayOptions: selectedView.displayOptions,
          });
          doSearch();
          handleClose();
        }
      },
    },
  };

  const {
    heading,
    description,
    readOnly = false,
    buttonText,
    onConfirm,
    disabled,
    textFieldProps,
  } = config[variant] || {};

  return (
    <Dialog
      css={{ width: '520px', maxWidth: 'none' }}
      isOpen={!!variant}
      onClose={handleClose}
    >
      <DialogHeader>{heading}</DialogHeader>
      <DialogContent>
        <Typography as="p" variant="subtext" css={{ mb: '$medium' }}>
          {description}
        </Typography>
        <TextField
          label="Name"
          maxLength={40}
          as="input"
          placeholder={t('core.viewName')}
          onChange={(nextValue) => {
            setName(nextValue);
          }}
          css={{
            '& .manifest-textfield-base__icon--start': {
              height: '100%',
              width: '40px',
            },
            '& .manifest-form-control__label': {
              color: '$palette-grey-900',
            },
          }}
          startIcon={
            <Flex>
              <Box
                as="span"
                css={{
                  backgroundColor: isOrder
                    ? '$palette-pink-500'
                    : '$palette-blue-500',
                  borderRadius: '100%',
                  height: '12px',
                  width: '12px',
                }}
              />
            </Flex>
          }
          {...textFieldProps}
          {...(readOnly && {
            isReadOnly: true,
            isDisabled: true,
            value: selectedView?.name,
          })}
        />
      </DialogContent>
      <DialogFooter
        css={{
          '& .manifest-button-group': {
            alignItems: 'center',
          },
        }}
      >
        {loading && <Spinner data-testid="loading" size="medium" />}
        <Button variant="secondary" onPress={handleClose} isDisabled={loading}>
          {t('global.cancel')}
        </Button>
        <Button onPress={onConfirm} isDisabled={loading || disabled}>
          {buttonText}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export default ViewModal;
